<script setup>
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import api from "@/lib/axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Check, Star, ArrowRight, CheckCircle2 } from "lucide-vue-next";
import HeroTimelineMockup from "./HeroTimelineMockup.vue";

const { t, locale } = useI18n();
const { toast } = useToast();

// Inline email is a softer entry point than the demo form below — captures
// the visitor who's curious but not ready to fill a multi-field form.
const email = ref("");
const isSubmitting = ref(false);
const isSubmitted = ref(false);

const submitInlineEmail = async (e) => {
  e.preventDefault();
  if (!email.value || isSubmitting.value) return;
  isSubmitting.value = true;
  try {
    await api.post("/api/v1/public/demo-requests", {
      email: email.value,
      source: "hero",
      locale: locale.value,
    });
    isSubmitted.value = true;
  } catch (err) {
    toast({
      title: t("landing.hero.cta.error_title"),
      description: t("landing.hero.cta.error_description"),
      variant: "destructive",
    });
  } finally {
    isSubmitting.value = false;
  }
};

const resetHeroForm = () => {
  isSubmitted.value = false;
  email.value = "";
};
</script>

<template>
  <section class="relative pt-28 lg:pt-36 pb-16 lg:pb-24 bg-background overflow-hidden">
    <!-- Ambient backdrop, intentionally low-intensity in light mode -->
    <div class="absolute inset-0 cyber-grid opacity-30" />
    <div class="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[450px] bg-accent/5 rounded-full blur-3xl" />

    <div class="container relative z-10 mx-auto px-6">
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <!-- Copy column. 7/12 leaves room for a roomy headline. -->
        <div class="lg:col-span-7 max-w-2xl">
          <!-- Microbadge with concrete stat — gradient + inner highlight
               for "premium" depth, keeps the 5-star visual anchor. -->
          <div
            class="inline-flex items-center gap-2 px-3 py-1.5 mb-6 rounded-full border border-border text-xs font-medium"
            style="
              background: linear-gradient(180deg, hsl(var(--card)) 0%, hsl(var(--muted) / 0.7) 100%);
              box-shadow: inset 0 1px 0 hsl(0 0% 100% / 0.5), 0 1px 2px hsl(214 28% 30% / 0.05);
            "
          >
            <span class="flex items-center gap-0.5 text-amber-500">
              <Star v-for="i in 5" :key="i" class="w-3 h-3 fill-current" />
            </span>
            <span class="text-muted-foreground">
              {{ t("landing.hero.microbadge") }}
            </span>
          </div>

          <!-- H1 with typographic POV: emphasized positive word in italic
               cyan, negated word with hand-drawn strikethrough.
               Whitespace lives in the i18n strings (pre/mid/post), never
               in the template, so Vue's compiler can't strip it. -->
          <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-[1.05] tracking-tight">{{ t("landing.hero.title_pre") }}<span class="word-emph text-accent">{{ t("landing.hero.title_emph") }}</span>{{ t("landing.hero.title_mid") }}<span class="word-strike text-destructive">{{ t("landing.hero.title_strike") }}</span>{{ t("landing.hero.title_post") }}</h1>

          <p class="text-lg md:text-xl text-muted-foreground mb-8 leading-relaxed">{{ t("landing.hero.subtitle_pre") }}<span class="word-emph text-accent">{{ t("landing.hero.subtitle_emph") }}</span>{{ t("landing.hero.subtitle_post") }}</p>

          <!-- Inline email primary entry + secondary ghost.
               The inline pattern (Harvest, TimeCamp) reduces friction vs.
               sending the visitor down to the contact form. After submit
               the form swaps for a small confirmation card so the visitor
               doesn't think "I clicked but nothing happened". -->
          <form
            v-if="!isSubmitted"
            @submit="submitInlineEmail"
            class="flex flex-col sm:flex-row gap-3 mb-5"
          >
            <Input
              v-model="email"
              type="email"
              :placeholder="t('landing.hero.email_placeholder')"
              class="h-12 sm:max-w-xs bg-card border-border"
              required
              :disabled="isSubmitting"
            />
            <Button
              type="submit"
              variant="hero-primary"
              size="xl"
              class="w-full sm:w-auto"
              :disabled="isSubmitting"
            >
              <span v-if="isSubmitting" class="flex items-center gap-2">
                <span class="w-4 h-4 border-2 border-foreground/30 border-t-foreground rounded-full animate-spin" />
                {{ t("landing.hero.cta.submitting") }}
              </span>
              <span v-else class="flex items-center gap-2">
                {{ t("landing.hero.cta.primary") }}
                <ArrowRight class="w-4 h-4" />
              </span>
            </Button>
          </form>

          <div
            v-else
            class="flex items-start gap-3 p-4 mb-5 rounded-lg border border-accent/40 bg-accent/5"
            role="status"
            aria-live="polite"
          >
            <CheckCircle2 class="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-foreground mb-0.5">
                {{ t("landing.hero.cta.success_title") }}
              </p>
              <p class="text-sm text-muted-foreground">
                {{ t("landing.hero.cta.success_description") }}
              </p>
            </div>
            <button
              type="button"
              class="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
              @click="resetHeroForm"
            >
              {{ t("landing.hero.cta.success_send_another") }}
            </button>
          </div>

          <!-- Secondary ghost link (kept subordinate to avoid dual-primary error) -->
          <a
            href="#how-it-works"
            class="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
          >
            {{ t("landing.hero.cta.secondary") }}
            <ArrowRight class="w-3.5 h-3.5" />
          </a>

          <!-- Trust row as horizontal chips. The third chip is our unique
               differentiator vs. all 10 competitors analysed: nobody else
               surfaces "the employee always knows what's captured" here. -->
          <ul class="flex flex-wrap gap-x-5 gap-y-2 text-sm text-foreground">
            <li class="flex items-center gap-1.5">
              <Check class="w-4 h-4 text-accent flex-shrink-0" />
              <span>{{ t("landing.hero.trust.0") }}</span>
            </li>
            <li class="flex items-center gap-1.5">
              <Check class="w-4 h-4 text-accent flex-shrink-0" />
              <span>{{ t("landing.hero.trust.1") }}</span>
            </li>
            <li class="flex items-center gap-1.5">
              <Check class="w-4 h-4 text-accent flex-shrink-0" />
              <span class="font-medium">{{ t("landing.hero.trust.2") }}</span>
            </li>
          </ul>
        </div>

        <!-- Visual column: timeline heatmap + floating cards.
             More distinctive than a generic screenshot — the WORK/IDLE/OFF
             grid is the product's signature visual surface. -->
        <div class="lg:col-span-5">
          <div class="relative">
            <div class="absolute -inset-8 bg-accent/10 rounded-3xl blur-3xl opacity-60" />
            <HeroTimelineMockup class="relative" />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
